import { Injectable } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';
import { GridRow } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { TranslateService } from '@app/core/services/translate.service';
import { GraphMailService } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { TypeaheadService } from '@app/services/typeahead.service';
import { Attachment, FileAttachment, ItemAttachment, Message, ReferenceAttachment } from '@microsoft/microsoft-graph-types';
import { BehaviorSubject } from 'rxjs';
@Injectable({
    providedIn: 'root',
})
export class EmailService {
    public initialMailFoldersLoaded$ = new BehaviorSubject(false);

    private base64?: any;

    constructor(
        private datatask: DataTaskService,
        private typeahead: TypeaheadService,
        private translate: TranslateService,
        private graphMail: GraphMailService,
        private upgrade: UpgradeModule,
    ) {}

    /**
     * Enriches the emails given in the parameters according to the settings given in the parameters.
     *
     * @param params the emails and settings to use when enriching.
     * @returns a promise resolving in a list of enriched emails.
     */
    public async enrichEmails(params: EnrichEmailsParams): Promise<EnrichedEmail[]> {
        const chunks = this.getEmailChunks(params.emails, params.chunkSize);

        let data = await Promise.all(chunks.map((chunk) => this.datatask.Post(2950, { ...params, emails: chunk }) as Promise<EnrichedEmail[]>));
        let flatdata = data.flat();
        // return flatdata;
        return flatdata.map((item) => {
            let mailinfo = item.compressed_mailinfo;

            try {
                mailinfo = this.getBase64().urldecode(mailinfo);
            } catch (error) {
                //
            }

            return { ...item, compressed_mailinfo: mailinfo };
        });
    }

    /**
     * Enriches the given emails according to the settings of the parameters, and merges the enriched email to the messages post enriching.
     *
     * @param params the emails and settings to use for enriching.
     * @returns a promise resolving in a list of where enriched emails and messages are merges based on their id.
     */
    public async getEnrichedMessages(params: EnrichEmailsParams): Promise<EnrichedMessage[]> {
        const enrichedEmails = await this.enrichEmails(params);

        return params.emails.map((message) => {
            const email = enrichedEmails.find((enrichedEmail) => enrichedEmail.id === message.id);

            if (!!email) {
                console.table(message);
                console.log(message.datetime_sent);
                console.log(this.getDateInLocalTime(message.datetime_sent ?? ''));
                return { ...message, ...email };
                // return { ...message, ...email, datetime_sent: this.getDateInLocalTime(message.datetime_sent ?? ''), datetime_received: this.getDateInLocalTime(message.datetime_received ?? '') };
            }

            throw Error(`Could not find enriched email matching the id of "${message.id}"`);
        });
    }

    /**
     * Splits the given emails into chunks of emails based on the given chunck size.
     *
     * @param emails the emails to split into chunks.
     * @param chunkSize the max size of each chunks.
     * @returns a list of email chunks.
     */
    private getEmailChunks(emails: Partial<MessageEmail>[], chunkSize: number = 500): Partial<MessageEmail>[][] {
        let chunks: Partial<MessageEmail>[][] = [];
        let numberOfChunks = 0;
        let deepCopyEmails = emails.map(this.serializeMessageEmail);

        while (deepCopyEmails.length > 0) {
            chunks[numberOfChunks] = deepCopyEmails.splice(0, chunkSize);
            numberOfChunks++;
        }

        return chunks;
    }

    /**
     * Creates a deep copy of the given message, and removes unnecessary properties to reduce payload of request calls.
     *
     * @param message the message to serialize before enriching.
     * @returns a deep copy of the message with unecessary properties for enriching removed.
     */
    private serializeMessageEmail = (message: Partial<MessageEmail>) => {
        const email: Partial<MessageEmail> = Object.assign({}, message);
        // delete email.email_body;
        delete email.email_body_html;
        delete email.webLink;
        return email;
    };

    /**
     * Retrieves messages from microsoft graph api and maps them into a TouchTime friendly format.
     *
     * @param params the request settings for retrieving messages from mgt.
     * @returns a promise resolving in a TouchTime version of microsoft messages.
     */
    public async getMessageEmails(params: GetMessageEmailsParams): Promise<MessageEmail[]> {
        const data = (await this.getEmailsFromMgt(params)).map(this.mapMessageToEmail);

        return data;
    }

    /**
     * Retrieves messages from microsoft.
     *
     * @param param0 request settings to use for retrieving messages from microsoft.
     * @returns a promise resolving to a list of message.
     */
    private async getEmailsFromMgt({ folderId, limit, userId, filter, orderBy, search }: GetMessageEmailsParams): Promise<Message[]> {
        console.log('getEmailsFromMgt');
        let messages: Message[] = [];

        try {
            if (!folderId && !!search) {
                // no local search is no folderId.
                messages = (await this.graphMail.getAllMessages({ limit: limit, userId: userId, filter: '', search: `"${search}"` })).value;
            } else if (!!folderId && !!search) {
                messages = (await this.graphMail.getMessagesFromFolder({ folderId: folderId, limit: limit, userId: userId, filter: filter, search: `"${search}"`, orderBy: orderBy ?? '' })).value;
            } else if (!!folderId) {
                const orderby = !!filter.includes('inferenceClassification') ? 'inferenceClassification' : '';
                messages = (await this.graphMail.getMessagesFromFolder({ folderId: folderId, limit: limit, userId: userId, search: '', filter: filter ?? '', orderBy: orderBy ?? orderby })).value;
            }
        } catch (error) {
            console.error(error);
            messages = [];
        }

        return messages;
    }

    private mapMessageToEmail = (message: Message): MessageEmail => {
        let recipientEmail = '';
        let recipientName = '';
        let ccRecipients = '';

        message?.toRecipients?.forEach((recipient) => {
            recipientEmail += recipient?.emailAddress?.address + ', ';
            recipientName += recipient?.emailAddress?.name + ', ';
        });

        message?.ccRecipients?.forEach((email) => (ccRecipients += email?.emailAddress?.address + ', '));

        return {
            id: message.id!,
            recipient_email: recipientEmail.substring(0, recipientEmail.length - 2),
            recipient_name: recipientName.substring(0, recipientName.length - 2),
            email_cc: ccRecipients?.substring(0, ccRecipients.length - 2),
            heading: message.subject ?? '',
            email_body: message.bodyPreview /*? this.getBase64().urlencode(message.bodyPreview) :*/ ?? '',
            email_body_html: !!message.body?.content ? this.getBase64().urlencode(message.body.content) : '',
            webLink: message.webLink ?? '',
            hasAttachments: message.hasAttachments ?? false,
            datetime_sent: this.getDateInLocalTime(message.sentDateTime ?? ''),
            datetime_received: this.getDateInLocalTime(message.receivedDateTime ?? ''),
            read_ok: message.isRead ?? false,
            is_selected: false,
            sender_email: message?.from?.emailAddress?.address ?? '',
            sender_name: message?.from?.emailAddress?.name ?? '',
            importance: message.importance ?? 'normal',
            conversationId: message.conversationId ?? '',
            sentDateTimeZulu: message.sentDateTime ?? '',
        };
    };

    private getDateInLocalTime(datestring?: string | null) {
        if (!!datestring && new Date(datestring).toString() !== 'Invalid Date') {
            const date = new Date(datestring);
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        }
        return '';
    }

    public async getEmailPageLoad(): Promise<EmailGet> {
        return (await this.datatask.Post(2942))[0];
    }

    public async saveEmailItem(email: EnrichedEmail): Promise<{ errorcode: string; errormessage: string; email_keyno_new: string | number; order_internal_no: string | number }> {
        return (await this.datatask.Post(2951, email))[0];
    }

    public serializeEmail(emailToSave: EnrichedMessage) {
        if (!!emailToSave) {
            let email: any = Object.assign({}, { ...emailToSave, email_body: this.getBase64().urlencode(emailToSave.email_body), heading: this.getBase64().urlencode(emailToSave.heading), custact_note_new: this.getBase64().urlencode(emailToSave.custact_note_new) });
            ['_events', '_handlers', 'parent', 'dirty', '_dirty', 'dirtyFields', 'uid', '_uuid', '_saveFailed'].forEach((key) => delete email[key]);
            return email;
        }

        throw Error('Email must be defined');
    }

    public getSharedMailboxes(emailAccount: string): Promise<Item[]> {
        return this.datatask.Post(2957, { current_account_email: emailAccount });
    }

    public async getCustomerFromCustact(custactNo: string | number): Promise<CustomerFromCustact> {
        return (await this.datatask.Post(2725, { custact_no: custactNo }))[0];
    }

    public async getProject(projectKeyno: string | number): Promise<Project> {
        return (await this.datatask.Post(905, { project_keyno: projectKeyno }))[0];
    }

    public async getProjects(searchtext: string, custNo: string | number): Promise<Item[]> {
        if (custNo === '0') {
            return this.datatask.Post(902, { bg_projects_activestatus: 'ACTIVE', searchtext: searchtext ?? '', argtype: '0', argvalue: '0' });
        }

        return this.datatask.Post(902, { bg_projects_activestatus: 'ACTIVE', searchtext: searchtext ?? '', argtype: 'cust_no', argvalue: custNo ?? '0' });
    }

    public getCustactMembers(): Promise<Item[]> {
        return this.datatask.Post(95, { custact_no: '0', user_name: '' });
    }

    public searchAllCustomer(value: string): Promise<Item[]> {
        return this.typeahead.searchCustomer({
            cust_name: value || '',
            custtype: 'A',
            lastselect: 'SEARCH',
        });
    }

    /**
     *
     * @param searchtext
     * @param custNo
     * @datatask 2772
     * @datataskName sp_ws_p2_ordersearch_typeahead_list
     * @returns
     */
    public searchOrders(searchtext: string, custNo?: string | number): Promise<OrderSearchItem[]> {
        return this.datatask.Post(2772, { searchtext: searchtext, cust_no: custNo });
    }

    /**
     *
     *
     * @param searchtext
     * @param custNo
     * @datatask 2955
     * @datataskName sp_ws_p2_purchhead_typeahead_list
     * @returns
     */
    public searchPurchases(searchtext: string, custNo?: string | number): Promise<PurchSearchItem[]> {
        return this.datatask.Post(2955, { searchtext: searchtext, cust_no: custNo });
    }

    public getCustomerPersons(custNo: string | number): Promise<Item[]> {
        return this.datatask.Post(71, { cust_no: custNo });
    }

    public getEmailAmountSizes(): Item[] {
        return [
            { item_id: '25', item_name: '25' },
            { item_id: '50', item_name: '50' },
            { item_id: '75', item_name: '75' },
            { item_id: '100', item_name: '100' },
            { item_id: '200', item_name: '200' },
            { item_id: '500', item_name: '500' },
            { item_id: '1000', item_name: '1000' },
        ];
    }
    private folderSearchOptions?: Item[];

    public async getFolderSearchOptions() {
        if (!this.folderSearchOptions) {
            const data = await this.translate.translateBatch(['email_search_all_folders', 'email_search_current_folder'] as const);

            this.folderSearchOptions = [
                { item_name: data.email_search_all_folders, item_id: '0' },
                { item_name: data.email_search_current_folder, item_id: '1' },
            ];
        }

        return this.folderSearchOptions!;
    }

    public getBase64(): any {
        if (!this.base64) {
            const $injector = this.upgrade.injector.get('$injector');
            this.base64 = $injector.get('base64');
        }
        return this.base64;
    }
}
export interface GetMessageEmailsParams {
    /**
     * No id provided will query all emails.
     */
    folderId?: string;
    limit: number;
    userId?: string;
    filter: string;
    orderBy?: string;
    search: string;
}
export interface EnrichEmailsParams {
    /**
     * The currently selected businessco.
     */
    businessco_no: string;

    /**
     * The currently used email account.
     */
    current_account_email: string;

    /**
     * List of messages parsed to Touchtime format that are to be enriched with additional info from TouchTime.
     */
    emails: MessageEmail[];

    /**
     * The size to split the emails sent to the enrich call.
     *
     * @default 500
     */
    chunkSize?: number;
}

export interface EmailGet {
    current_account_email: string;
    retrieve_mail_from_dt: string;
    selected_folder_name: string;
    selected_folder_id: string;
    auto_archieve_folder_id: string;
    auto_archieve_folder_name: string;
    move_to_folder_id: string;
    move_to_folder_name: string;
    current_mailbox: string;
    retrieve_max_nbr_of_emails: string;
    show_buttons_accounting: '1' | '0';
    heading_email_archiving: string;
    email_search_global: string;
    local_search: '1' | '0';
    businessco_no: string;
    show_setting: '1' | '0';
    folders_width: string;
    folders_is_expanded: '1' | '0';
    gb_email_main: string;
}

export interface MessageEmail {
    /**
     * Unique id for this email.
     */
    id: string;

    /**
     * The recipients emails, either a single one or multiple in a string seperated by `', '`.
     */
    recipient_email: string;

    /**
     * The recipients names, either a single one or multiple in a string seperated by `', '`.
     */
    recipient_name: string;
    /**
     * Emails addresses added on as copy, either a single one or multiple in a string seperated by `', '`.
     */
    email_cc: string;

    /**
     * The heading of the email.
     */
    heading: string;

    /**
     * A short preview of the email contents.
     */
    email_body: string;

    /**
     * Base64 encoded content of the email.
     */
    email_body_html: string;

    /**
     * Link to open the email in a webview for office 365.
     */
    webLink: string;

    /**
     * Whether the email has any **non-inline** attachments.
     */
    hasAttachments: boolean;

    /**
     * The timestamp for the when the email was sent.
     */
    datetime_sent: string;

    /**
     * The timestamp for the when the email was received.
     */
    datetime_received: string;

    /**
     * Whether the email is marked as read.
     */
    read_ok: boolean;

    /**
     * Whether the email has been selected.
     */
    is_selected: boolean;

    /**
     * The email of the sender.
     */
    sender_email: string;

    /**
     * The name of the sender.
     */
    sender_name: string;

    /**
     * The importance of the email.
     */
    importance: 'low' | 'normal' | 'high';

    /**
     * The id of the conversation (email thread) this email belongs to.
     */
    conversationId: string;

    /**
     * The string of the sentdatetime used to identify the grid.
     */
    sentDateTimeZulu: string;
}
export interface EnrichedEmail {
    /**
     * Unique id for this email.
     */
    id: string;
    cust_no: string | number;
    cust_name: string;
    pers_no: string;
    pers_name: string;
    custact_no: string | number;
    custact_note: string;
    create_new_custact: '1' | '0';
    custact_note_new: string;
    activitytype_no: string;
    project_keyno: string | number;
    project_name: string;
    projectprosess_keyno: string | number;
    prosess_name: string;
    order_internal_no: string | number;
    order_search_info: string;
    purch_internal_no: string | number;
    purch_search_info: string;

    participant_pu_keynos: string;
    participant_pu_names: string;
    compressed_mailinfo: string;

    /**
     * The portal user keyno for the user responsible for the custact on this email.
     */
    portal_user_keyno_responsible: string;

    /**
     * Whether to create a voucher from this email.
     */
    create_voucher: '1' | '0';
    create_offer: '1' | '0';

    /**
     * The id of the customer in touchtime this email was sent from.
     */
    from_cust_no: string | number;

    /**
     * The name of the customer in touchtime this email was sent from.
     */
    from_cust_name: string;

    /**
     * The email of the sender.
     */
    sender_email_new: string;

    /**
     * The name of the sender.
     */
    sender_name_new: string;

    /**
     * Space sepeared list of icons to display for this email in grid.
     */
    item_glyphicon: string;

    /**
     * Space seperated list of color to use for the space seperated icon list.
     */
    item_glyphicon_color: string;

    /**
     * Id of the company this email may be related to.
     */
    businessco_no: string;

    exist_in_email_archive: '1' | '0' | 1 | 0;

    email_keyno: `${number}` | number;

    /**
     * Related to grid use.
     */
    _dirty: false;

    /**
     * Related to grid use.
     */
    _uuid: string;
}

export type EnrichedMessage = EnrichedEmail & MessageEmail & GridRow & { attachments?: (Attachment | FileAttachment | ReferenceAttachment | ItemAttachment)[] };

export interface Project {
    project_keyno: string | number;
    project_name: string;
    active: '1' | '0';
    cust_no: string | number;
    cust_name: string;
    businessco_no: string | number;
    description: string;
    nbr_of_projectprosess: string | number;
    activities_badge: string;
    orders_badge: string;
    sum_ordervalue_locval: string | number;
    sum_purchasevalue_locval: string | number;
    sum_hours_total: string | number;
    purchases_badge: string;
    nbr_of_documents: string | number;
    nbr_of_pictures: string | number;
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_timesheetdiary: '1' | '0';
    show_button_gantt: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_paymentplans: '1' | '0';
    webpage_heading: string;
    gb_projectmenu_main: string;
    nbr_of_docpacktypes: string | number;
    docpacktype_keyno: string | number;
}

export interface CustomerFromCustact {
    cust_no: string;
    cust_name: string;
    project_keyno: string;
    projectprosess_keyno: string;
    custact_note: string;
    powtime_description: string;
    po_keyno: string;
    activitytype_no: string;
    actstatus_keyno: string;
    custact_no: string;
    project_name: string;
    prosess_name: string;
}

export interface EmailArchivedEvent {
    email: EnrichedMessage;
    email_keyno_new: string | number;
    errorcode: string;
    errormessage: string;
    goto_reply: '1' | '0';
    reply_all: '1' | '0';
    order_internal_no: string | number;
    event: MouseEvent;
}

// All types are currently `string` because all? return data from database is string
export interface OrderSearchItem {
    order_internal_no: string;
    item_name: string;
    orderby: string;
    cust_no: string;
    cust_name: string;
    project_keyno: string;
    project_name: string;
    order_order_no: string;
    order_offer_no: string;
}

export interface PurchSearchItem {
    purch_internal_no: string;
    item_name: string;
    orderby: string;
    cust_no: string;
    cust_no_name: string;
}
